import React, { useCallback, useRef, useState } from "react";

// Shared document-upload tile used by both the Doctor KYC card (Profile &
// Account) and the compliance credentialing cards (Clinic Settings →
// Compliance & Verification), so both flows look and behave identically.

export type DocStatus = "verified" | "submitted" | "rejected" | "pending";

export interface DocState {
    status: DocStatus;
    fileName?: string;
    uploadedAt?: string;       // display string, e.g. "3 May 2026"
    docId?: string;
    rejectionReason?: string;
    viewUrl?: string | null;   // short-lived presigned GET URL
    uploading?: boolean;
}

export interface DocTileInfo {
    id: string;
    label: string;
    description: string;
    /** Whether this document is required (vs. optional). Omit to always show "Optional". */
    required?: boolean;
}

export const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
export const ACCEPTED_TYPES = ["application/pdf", "image/png", "image/jpeg", "image/jpg"];

export function formatDate(iso?: string | null): string {
    const d = iso ? new Date(iso) : new Date();
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

/** Backend status → tile DocStatus. */
export function toDocStatus(s: "submitted" | "verified" | "rejected"): DocStatus {
    return s === "verified" ? "verified" : s === "rejected" ? "rejected" : "submitted";
}

export async function putToS3(uploadUrl: string, file: File): Promise<void> {
    const res = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
        signal: AbortSignal.timeout(60_000),
    });
    if (!res.ok) throw new Error(`S3 upload failed: HTTP ${res.status}`);
}

/* ------------------------------------------------------------------ */
/*  Icons                                                              */
/* ------------------------------------------------------------------ */

const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

export const FileIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
    </svg>
);

export const UploadIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
);

/* ------------------------------------------------------------------ */
/*  Status badge                                                       */
/* ------------------------------------------------------------------ */

export const StatusBadge: React.FC<{ status: DocStatus }> = ({ status }) => {
    if (status === "verified") {
        return (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-600 whitespace-nowrap">
                <CheckIcon className="w-3 h-3" /> Verified
            </span>
        );
    }
    if (status === "submitted") {
        return (
            <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-600 whitespace-nowrap">
                Submitted
            </span>
        );
    }
    if (status === "rejected") {
        return (
            <span className="inline-flex items-center rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-600 whitespace-nowrap">
                Rejected
            </span>
        );
    }
    return (
        <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-600 whitespace-nowrap">
            Pending upload
        </span>
    );
};

export const RequiredPill: React.FC<{ required?: boolean }> = ({ required }) =>
    required ? (
        <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700 whitespace-nowrap">
            Required
        </span>
    ) : (
        <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500 whitespace-nowrap">
            Optional
        </span>
    );

/* ------------------------------------------------------------------ */
/*  Document tile                                                      */
/* ------------------------------------------------------------------ */

interface DocTileProps {
    doc: DocTileInfo;
    state: DocState;
    disabledReason?: string;
    /** View-only access: hide the dropzone and Replace/Update, keep View. */
    readOnly?: boolean;
    onFileSelect: (id: string, file: File) => void;
    onView: (id: string) => void;
}

export const DocTile: React.FC<DocTileProps> = ({ doc, state, disabledReason, readOnly, onFileSelect, onView }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [dragOver, setDragOver] = useState(false);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) onFileSelect(doc.id, file);
    }, [doc.id, onFileSelect]);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) onFileSelect(doc.id, file);
        if (inputRef.current) inputRef.current.value = "";
    }, [doc.id, onFileSelect]);

    // A file is "on file" once submitted or verified. Rejected docs fall back to
    // the upload dropzone so the clinic can re-submit a corrected copy.
    const hasFile = state.status === "verified" || state.status === "submitted";
    // Reuses the existing disabledReason panel rather than adding a second
    // "locked" style.
    const lockedReason = readOnly
        ? "You have view-only access to this section."
        : disabledReason;

    return (
        <div className="rounded-xl border border-slate-200 p-4">
            {/* Always-mounted picker so the dropzone AND the Replace/Update button
                can open it directly — replacing no longer clears the tile first, so
                cancelling the OS dialog leaves the existing document in place. */}
            <input
                ref={inputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={handleChange}
                className="hidden"
            />
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-slate-900">{doc.label}</p>
                        <RequiredPill required={doc.required} />
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{doc.description}</p>
                </div>
                {/* An optional doc with nothing uploaded isn't "pending" anything —
                    the Optional pill already says all there is to say. */}
                {(doc.required || state.status !== "pending" || state.uploading) && (
                    <StatusBadge status={state.uploading ? "submitted" : state.status} />
                )}
            </div>

            {state.status === "rejected" && state.rejectionReason && (
                <div className="mt-3 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-[11px] text-red-700">
                    Rejected: {state.rejectionReason}
                </div>
            )}

            {hasFile ? (
                <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5 flex items-center gap-2.5">
                    <FileIcon className={`w-4 h-4 flex-shrink-0 ${state.status === "verified" ? "text-emerald-500" : "text-blue-500"}`} />
                    <div className="min-w-0 flex-1">
                        <button
                            type="button"
                            onClick={() => onView(doc.id)}
                            className="text-xs font-medium truncate text-left text-blue-600 hover:underline"
                        >
                            {state.fileName ?? "View document"}
                        </button>
                        <p className="text-[11px] text-slate-400">
                            {state.uploading
                                ? "Uploading…"
                                : `Uploaded ${state.uploadedAt}${state.status === "verified" ? " · Verified" : " · Awaiting review"}`}
                        </p>
                    </div>
                    {/* Replace a submitted doc, or Update an already-verified one.
                        Both open the picker directly; the parent confirms before
                        replacing a verified doc since it re-enters review. */}
                    {!readOnly && (state.status === "submitted" || state.status === "verified") && !state.uploading && (
                        <button
                            type="button"
                            onClick={() => inputRef.current?.click()}
                            className="text-[11px] font-medium text-slate-400 hover:text-blue-600 flex-shrink-0"
                            aria-label={`${state.status === "verified" ? "Update" : "Replace"} ${doc.label}`}
                        >
                            {state.status === "verified" ? "Update" : "Replace"}
                        </button>
                    )}
                </div>
            ) : lockedReason ? (
                <div className="mt-3 w-full rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-400">
                    {lockedReason}
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                    className={`mt-3 w-full rounded-lg border border-dashed px-3 py-3 flex items-center gap-2.5 text-left transition-colors ${
                        dragOver ? "border-blue-400 bg-blue-50" : "border-slate-300 bg-white hover:bg-slate-50"
                    }`}
                >
                    <UploadIcon className="w-4 h-4 flex-shrink-0 text-slate-400" />
                    <span className="text-xs text-slate-500">
                        <span className="font-medium text-slate-600">Click to upload</span> or drag and drop
                        <span className="block text-[11px] text-slate-400 mt-0.5">PDF, JPG, PNG · max 5 MB</span>
                    </span>
                </button>
            )}
        </div>
    );
};
